import { ComposeStepState, IComposeStep } from ".";
import { is, List } from "immutable";
import _ from "lodash";
import * as React from "react";
import * as BS from "react-bootstrap";
import { CoAuthorsEditor } from "./CoAuthorsEditor";
import { ContinueButtons } from "./ContinueButtons";
import { ComposeStepProps } from "./IComposeStep";
import * as Comp from "../../../../components";
import { ComponentWithValidatedForm } from "../../../../components/validation";
import { AllowedImageUploadExtns } from "../../../../constants";
import * as Forms from "../../../../forms";
import * as M from "../../../../models";
import { TinymceOptions } from "../../../../options";
import { Api } from "../../../../services/Api";
import { LegacyApi } from "../../../../services/LegacyApi";
import { ReactHelper, Utility } from "../../../../utils";

type OwnProps = ComposeStepProps & {
    countries: List<M.Country.Country>;
    onForceAuthRefresh: () => void;
    preSelectedFormat?: string;
    submittingUser: M.User.BasicProfile;
    types: List<M.Abstract.AbstractType>;
    userStats?: M.Abstract.UserStats;
};

type OwnState = ComposeStepState & {
    abstractTypeStat?: { allowed: number, submitted: number, typeName: string };
    changeProfilePhoto: boolean;
    primaryAuthor?: M.Abstract.AbstractAuthor;
    showTypeChangeWarning: boolean;
    typeChangeAccepted: boolean;
};

export class AuthorshipForm extends ComponentWithValidatedForm<OwnProps, OwnState> implements IComposeStep {

    private _isMounted = false;
    private _searchTimeout: NodeJS.Timer;

    public constructor(props: any) {
        super(props);
        const formModel = new Forms.Abstract.ComposeAuthorship();
        this.state = {
            ...this.getInitialState(formModel),
            changeProfilePhoto: false,
            isSubmitLater: false,
            showTypeChangeWarning: false,
            typeChangeAccepted: false,
        };
    }

    public componentDidMount() {
        this._isMounted = true;
        if (this.props.abstract)
            this.setFormData(this.props.abstract);

        this.initialChecks();
    }

    public componentDidUpdate(prevProps: Readonly<OwnProps>) {
        this.initialChecks(prevProps);
    }

    public componentWillUnmount() {
        this._isMounted = false;
    }

    public setFormData(abstract: M.Abstract.Abstract) {
        const formModel = this.getFormModel() as Forms.Abstract.ComposeAuthorship;
        formModel.data.abstractId = abstract.id;
        formModel.data.keywords = abstract.keywords.map((x) => ({
            value: x,
            label: x,
        })),
        formModel.data.typeId = abstract.typeId;
        formModel.data.title = abstract.title || "";
        formModel.data.studyGroup = abstract.studyGroup || "";

        formModel.data.authors = abstract.authors
            .filter((x) => !x.isPrimary)
            .map((x, i) => ({
                deleted: false,
                email: x.email,
                firstName: x.firstName || "",
                id: x.id,
                index: i,
                isMember: x.isMember,
                lastName: x.lastName || "",
                profilePicture: x.profilePicture,
                title: x.title,
                userId: x.userId,
            }));

        const primaryAuthor = _.find(abstract.authors, (x) => x.isPrimary);
        if (primaryAuthor) {
            formModel.data.displayProfilePicture = primaryAuthor.showProfilePicture;
            if (primaryAuthor.phone)
                formModel.data.phone = Forms.Generic.PhoneModelToForm(primaryAuthor.phone);
        }

        this.setState({
            ...this.state,
            formModel,
            primaryAuthor,
        });
    }

    public render() {
        const isNew = !this.props.abstract;
        const isMember = this.props.submittingUser.isMember;
        const formModel = this.getFormModel() as Forms.Abstract.ComposeAuthorship;
        const presentingAuthor = this.state.primaryAuthor
            ? <Comp.Reusable.PersonSearchListItem
                avatar={M.Generic.PersonHelper.getAvatarUrl(this.state.primaryAuthor)}
                name={M.Generic.PersonHelper.getFullName(this.state.primaryAuthor)}
                title={this.state.primaryAuthor.title}
                email={this.state.primaryAuthor.email}
            />
            : <Comp.Reusable.PersonSearchListItem
                avatar={M.Generic.PersonHelper.getAvatarUrl(this.props.submittingUser)}
                name={M.Generic.PersonHelper.getFullName(this.props.submittingUser)}
                title={this.props.submittingUser.degrees}
                email={this.props.submittingUser.username}
            />;

        let submitingProfilePicture = this.state.primaryAuthor
            ? M.Generic.PersonHelper.getAvatarUrl(this.state.primaryAuthor)
            : M.Generic.PersonHelper.getAvatarUrl(this.props.submittingUser);
        if (submitingProfilePicture.indexOf("asrs.org/content") > -1)
            submitingProfilePicture += "/image-custom;ratio_crop$100,130;max$130,130.ImageHandler";

        const types: M.Editors.SelectItem[] = this.props.types
            .filter((x: M.Abstract.AbstractType) => !x.isHiddenOnForm)
            .map((x: M.Abstract.AbstractType) => {
                const isDisabled = !isMember && x.membersOnly;

                return {
                    disabled: isDisabled,
                    id: x.id,
                    render: x.name + (isDisabled ? "*" : ""),
                    selected: false,
                };
            })
            .toArray();

        const typeFieldLabel = this.getFieldLabel("typeId");
        const typeStat = this.state.abstractTypeStat;
        const typeErrors = this.getFieldErrors("typeId");
        const typeField = isNew || this.state.typeChangeAccepted
            ? <Comp.Editors.RadioOrCheckListField
                formComponent={this}
                name="typeId"
                hint={isMember ? "" : "*ASRS members only"}
                items={types}
                type="radio"
            >
                <BS.Collapse in={typeStat !== undefined}>
                    <BS.Alert bsStyle="warning">
                        <h3><Comp.Generic.Icon icon="warning" /> Warning</h3>
                        <p>
                            Only {Utility.numberToWord(typeStat && typeStat.allowed, true)} {typeStat && typeStat.typeName} may be submitted
                        </p>
                    </BS.Alert>
                </BS.Collapse>
            </Comp.Editors.RadioOrCheckListField>
            : <Comp.Editors.FormField name="" label={typeFieldLabel} required>
                <p>
                    {(this.props.abstract && this.props.abstract.type) || "Unknown presentation format"}{" "}
                    <span className="mute">
                        <a href="#" onClick={(e) => { ReactHelper.emptyClick(e); this.toggleTypeChangeWarning(); }}>(Change)</a>
                    </span>
                </p>

                <Comp.Editors.FormFieldErrors errors={typeErrors} />

                <input type="hidden" name="typeId" />

                <BS.Collapse in={this.state.showTypeChangeWarning}>
                    <BS.Alert bsStyle="warning">
                        <h3><Comp.Generic.Icon icon="warning" /> Warning</h3>
                        <p>Changing the presentation format will require you to re-enter abstract details as well as re-upload any media.</p>
                        <p>
                            <a href="#" onClick={(e) => { ReactHelper.emptyClick(e); this.toggleTypeChange(true); }}>Accept</a>
                            {" | "}
                            <a href="#" onClick={(e) => { ReactHelper.emptyClick(e); this.toggleTypeChange(false); }}>Cancel</a>
                        </p>
                    </BS.Alert>
                </BS.Collapse>
            </Comp.Editors.FormField>;

        return <Comp.Validation.ValidatedForm
            formComponent={this}
            model={this.state.formModel.data}
            onInputChange={(inputName) => this.onInputChangeHandler(inputName)}
            onValidSubmit={async (model) => this.handleSubmit(model)}
            schema={this.state.formModel.rules}
            ref={(ref: any) => this.form = ref}
        >
            <h1>Authorship</h1>

            {typeField}

            <Comp.Editors.RichTextField
                formComponent={this}
                name="title"
                description={<p className="mute">The title should appropriately describe the essence of the study. Capitalize major words within the title.</p>}
                options={TinymceOptions.specialCharacters({
                    characterlimit_max: 150,
                    statusbar: true,
                }, "charactercount characterlimit")}
            />

            <Comp.Editors.FormField name="" label="Presenting author" required>
                <div className="authors">
                    <ul>
                        <li>{presentingAuthor}</li>
                    </ul>
                </div>
            </Comp.Editors.FormField>

            <Comp.Editors.PhoneNumberField
                formComponent={this}
                name="phone"
                description={<p>
                    Mobile phone number ASRS staff may use to contact you on-site regarding Annual Meeting
                    issues related to presentations, such as last-minute schedule changes, peer review changes, etc.
                </p>}
                hideType
            />

            <CoAuthorsEditor
                formComponent={this}
                api={this.props.api}
                authors={formModel.data.authors}
                onAdd={(user) => this.onAuthorAdd(user)}
                onDelete={(index) => this.onAuthorDelete(index)}
            />

            <Comp.Editors.SelectField
                formComponent={this}
                isAsync
                description={<p>Please include three to five keywords that describe your abstract. These will be used to help attendees search by topic area/relevance.
                    Choose keywords from the suggest list or enter your own one at a time. <strong>Press enter after each keyword—do not separate with commas.</strong></p>}
                name="keywords"
                select={{
                    // cache: false,
                    arrowRenderer: () => null,
                    ignoreCase: false,
                    loadOptions: (search: string) => this.searchTags(search),
                    multi: true,
                    placeholder: "Examples: Retinablastoma Macular pucker Wet AMD",
                    searchPromptText: "Start typing and select from suggested or enter new tags",
                }}
            />

            <Comp.Editors.RichTextField
                formComponent={this}
                name="studyGroup"
                description={<p className="mute">Please include Study Group if these data entail clinical trial data. Example: MARINA, EVEREST, VIVID, DRCR.net</p>}
            />

            <Comp.Editors.FormField name="" label="Profile photo">
                <p>
                    <img alt="" src={submitingProfilePicture} style={{ width: "130px" }} />
                </p>

                <p>
                    <label>
                        <input type="checkbox" name="displayProfilePicture" checked={formModel.data.displayProfilePicture} onChange={() => {}} />{" "}
                        Display presenting author profile photo with abstract in annual meeting scientific program.
                    </label>
                </p>

                <p>
                    <a href="#" onClick={(e) => { ReactHelper.emptyClick(e); this.toggleChangeProfilePicture(); }}>Change your profile photo</a>
                </p>

                <BS.Collapse in={this.state.changeProfilePhoto}>
                    <div className="highlight">
                        <h3>Choose a new profile photo</h3>

                        <p className="mute">Allowed file extension(s): {Utility.joinSkipEmpty(AllowedImageUploadExtns, ", ")}</p>

                        <Comp.Upload.FileUploader
                            buttons={<BS.Button bsStyle="link" onClick={() => this.toggleChangeProfilePicture()}>Cancel</BS.Button>}
                            uploadOnSelect
                            uploader={{
                                filters: {
                                    max_file_size: "10mb",
                                    mime_types: [
                                        { title: "Images", extensions: Utility.joinSkipEmpty(AllowedImageUploadExtns, ",") },
                                    ],
                                    prevent_duplicates: true,
                                },
                                url: Api.endpoints.users.account.profilePicture.absoluteApiUrl(),
                            }}
                            onUploadComplete={(media) => this.onProfilePictureUploaded(media)}
                        />
                    </div>
                </BS.Collapse>
            </Comp.Editors.FormField>

            <ContinueButtons
                hideBack
                hideSkip={this.props.abstract === undefined}
                onBack={() => this.props.onBack()}
                onSave={(isSubmitLater) => this.onSaveHandler(isSubmitLater)}
                onSkip={() => this.props.onSkip()}
                processing={this.state.formProcessing}
            />

        </Comp.Validation.ValidatedForm>;
    }

    private checkAbstractTypeStats() {
        const formModel = this.getFormModel() as Forms.Abstract.ComposeAuthorship;
        const typeId = formModel.data.typeId;
        const type = this.props.types.find((x: M.Abstract.AbstractType) => x.id === typeId);
        const userStats = this.props.userStats;
        if (type && userStats) {
            const abstractId = this.props.abstract ? this.props.abstract.id : 0;
            const typeStat = _.find(userStats.abstractTypes, (t) => t.typeId === type.id);
            const catStat = _.find(userStats.abstractTypeCategories, (t) => t.typeId === type.categoryId);
            if (type.submissionsPerUser && typeStat
                    && typeStat.submittedAbstracts >= type.submissionsPerUser
                    && typeStat.submittedAbstractIds.indexOf(abstractId) === -1) {
                this.setState({ ...this.state, abstractTypeStat: {
                    allowed: type.submissionsPerUser,
                    submitted: typeStat.submittedAbstracts,
                    typeName: type.name,
                } });
                return;
            }
            if (type.categorySubmissionsPerUser && catStat
                && catStat.submittedAbstracts >= type.categorySubmissionsPerUser
                && catStat.submittedAbstractIds.indexOf(abstractId) === -1) {
                this.setState({ ...this.state, abstractTypeStat: {
                    allowed: type.categorySubmissionsPerUser,
                    submitted: catStat.submittedAbstracts,
                    typeName: type.categoryName || type.name,
                } });
                return;
            }
        }

        this.setState({ ...this.state, abstractTypeStat: undefined });
    }

    private initialChecks(prevProps?: Readonly<OwnProps>) {
        if (prevProps) {
            if ((!is(prevProps.types, this.props.types) || !is(prevProps.userStats, this.props.userStats)) && this.props.types.size && this.props.userStats) {
                this.checkAbstractTypeStats();
            }
        }
        else {
            if (this.props.types.size && this.props.userStats) {
                this.checkAbstractTypeStats();
            }
        }

        // preselect format as given in URL
        if (this.props.types.size && this.props.preSelectedFormat && !this.state.formModel.data.typeId) {
            this.preSelectFormat(this.props.preSelectedFormat);
        }
    }

    private onInputChangeHandler(inputName: string) {
        if (inputName === "typeId")
            this.checkAbstractTypeStats();
        else if (inputName === "keywords") {
            const model = this.getFormModelData<Forms.Abstract.ComposeAuthorshipData>();
            if (model.keywords) {
                const allAsStr = model.keywords.map((x: M.Generic.ReactSelectOption) => x.value).join(", ");
                const keywords = allAsStr.replace(/,\s{1,}/, ",").split(",").map((x) => x.trim()).filter((x) => x.length > 0);
                model.keywords = [];
                for (const t of keywords)
                    if (!_.find(model.keywords, (x: M.Generic.ReactSelectOption) => (x.value.toString()).toLowerCase() === t.toLowerCase()))
                        model.keywords.push(Utility.toReactSelectOption({}, t, t));
                this.updateFormModelData(model);
            }
        }
    }

    private onSaveHandler(isSubmitLater: boolean) {
        this.setState({ ...this.state, isSubmitLater }, () => {
            this.triggerSubmit();
        });
    }

    private preSelectFormat(format: string) {
        const type = this.props.types.find((t: M.Abstract.AbstractType) => Utility.toSlug(t.shortName) === Utility.toSlug(format));
        console.log(`Pre-selected abstract type (${format}):`, type);
        if (type) {
            const formModel = this.getFormModel() as Forms.Abstract.ComposeAuthorship;
            formModel.data.typeId = type.id;
            this.updateFormModel(formModel);
        }
    }

    private handleSubmit(model: Forms.Abstract.ComposeAuthorshipData) {
        const authors = model.authors
            .filter((x) => !(x.id <= 0 && x.deleted))
            .map((x) => ({
                deleted: x.deleted,
                displayProfilePicture: false,
                id: x.id,
                isPrimary: false,
                userId: x.userId,
                email: x.email,
                firstName: x.firstName,
                lastName: x.lastName,
                degrees: x.title,
                phone: undefined,
            }));

        if (this.state.primaryAuthor) {
            (authors as any).push({
                deleted: false,
                displayProfilePicture: model.displayProfilePicture,
                id: this.state.primaryAuthor.id,
                isPrimary: true,
                userId: this.state.primaryAuthor.userId,
                email: this.state.primaryAuthor.email,
                firstName: this.state.primaryAuthor.firstName,
                lastName: this.state.primaryAuthor.lastName,
                degrees: this.state.primaryAuthor.title,
                phone: {
                    number: model.phone.number,
                    countryCode: (model.phone.countryCode as any).value,
                },
            });
        }
        else {
            (authors as any).push({
                deleted: false,
                displayProfilePicture: model.displayProfilePicture,
                id: 0,
                isPrimary: true,
                userId: this.props.submittingUser.id,
                email: this.props.submittingUser.username,
                firstName: this.props.submittingUser.firstName,
                lastName: this.props.submittingUser.lastName,
                degrees: this.props.submittingUser.degrees,
                phone: {
                    number: model.phone.number,
                    countryCode: (model.phone.countryCode as any).value,
                },
            });
        }

        const body = {
            abstractId: model.abstractId,
            authors,
            eventId: this.props.eventId,
            guid: this.props.abstract ? this.props.abstract.guid : undefined,
            studyGroup: model.studyGroup,
            tags: (model.keywords || []).map((x: M.Generic.ReactSelectOption) => x.label),
            title: Utility.decodeHtml(model.title),
            typeId: model.typeId,
        };

        const authorsErrors = this.validateAuthors();
        if (!authorsErrors)
            return;

        // specific selected abstract type validations
        const type = this.props.types.find((x: M.Abstract.AbstractType) => x.id === (model.typeId || 0));
        if (type) {
            // validate that user is OK to submit an abstract for selected type
            if (this.props.userStats && (type.submissionsPerUser || type.categorySubmissionsPerUser)) {
                const abstractId = this.props.abstract ? this.props.abstract.id : 0;
                const typeStat = _.find(this.props.userStats.abstractTypes, (t) => t.typeId === type.id);
                const catStat = _.find(this.props.userStats.abstractTypeCategories, (t) => t.typeId === type.categoryId);
                if (typeStat && type.submissionsPerUser
                    && typeStat.submittedAbstracts >= type.submissionsPerUser
                    && typeStat.submittedAbstractIds.indexOf(abstractId) === -1) {
                    this.handleValidationErrors([{
                        inputName: "typeId",
                        errors: [`Only ${Utility.numberToWord(type.submissionsPerUser, true)} ${type.name} may be submitted`],
                    }], "typeId", true);
                    return;
                }
                if (catStat && type.categorySubmissionsPerUser
                    && catStat.submittedAbstracts >= type.categorySubmissionsPerUser
                    && catStat.submittedAbstractIds.indexOf(abstractId) === -1) {
                    this.handleValidationErrors([{
                        inputName: "typeId",
                        errors: [`Only ${Utility.numberToWord(type.categorySubmissionsPerUser, true)} ${type.categoryName} may be submitted`],
                    }], "typeId", true);
                    return;
                }
            }
        }

        this.saveAbstract(model, body);
    }

    private onAuthorAdd(user: M.Member.BasicProfile) {
        if (!user)
            return;

        const formModel = this.getFormModel() as Forms.Abstract.ComposeAuthorship;
        const existingAuthor = _.find(formModel.data.authors, (c) => !c.deleted
            && ((c.userId > 0 && c.userId === user.userId)
                || (c.firstName === user.firstName
                    && c.lastName === user.lastName
                    && c.email === user.email)));

        if (!existingAuthor) {
            formModel.data.authors.push({
                deleted: false,
                email: user.email || "",
                id: 0,
                index: formModel.data.authors.length,
                isMember: user.isMember,
                isNew: true,
                firstName: user.firstName || "",
                lastName: user.lastName || "",
                profilePicture: user.profilePicture || "",
                title: user.degrees || "",
                userId: user.userId,
            });
            this.updateFormModel(formModel, () => {
                this.validateAuthors();
            });
        }
    }

    private onAuthorDelete(index: number) {
        const formModel = this.getFormModel() as Forms.Abstract.ComposeAuthorship;
        const authors = _.find(formModel.data.authors, (c) => c.index === index);

        if (authors) {
            authors.deleted = true;
            this.updateFormModel(formModel, () => {
                this.validateAuthors();
            });
        }
    }

    private onProfilePictureUploaded(media: M.Upload.Media) {
        if (!media.url) {
            alert("Failed to upload profile picture");
            return;
        }

        // try load image
        const img = new Image();
        img.onload = () => {
            const primaryAuthor = this.state.primaryAuthor;
            if (primaryAuthor) {
                primaryAuthor.profilePicture = media.url;
                this.setState({ ...this.state, primaryAuthor, changeProfilePhoto: false }, () => {
                    this.props.onForceAuthRefresh();
                });

                // update legacy member profile picture
                const legacyApi = new LegacyApi();
                legacyApi.updateMemberProfilePicture(media.url || "").then();
            }
            else
                this.props.onForceAuthRefresh();
        };
        img.onerror = (err) => console.error("Failed to load", media, err);
        img.src = media.url;
    }

    private async saveAbstract(model: Forms.Abstract.ComposeAuthorshipData, body: any) {
        this.toggleProcessing(true);
        const result = await this.props.api.post(Api.endpoints.abstract.composeAuthorship, {
            body: JSON.stringify(body),
        });

        if (!this._isMounted)
            return;

        if (result.isSuccess && result.data) {
            this.props.onAbstractSaved(result.data, this.state.isSubmitLater);
        }
        else {
            this.setFormErrors(M.Result.Result.errorsOrMessage(result), undefined, true);
            this.toggleProcessing(false);
        }
    }

    private searchTags(search: string) {
        if (!search || search.length < 2)
            return Promise.resolve({ options: [] });

        return new Promise((resolve, reject) => {
            // delay the ajax call as to not make a call for each letter typed.
            if (this._searchTimeout !== undefined)
                clearTimeout(this._searchTimeout);

            this._searchTimeout = setTimeout(() => {
                const queryParams = { queryParams: { search, max: 10 }};

                // for adding new value
                const newTag: M.Tag.Tag & M.Generic.ReactSelectOption =
                    Utility.toReactSelectOption({ id: 0, statusId: 0, name: search }, search, search);

                this.props.api.callApi<M.Tag.Tag[]>(Api.endpoints.tag.tags, queryParams)
                    .then((result) => {
                        if (result.isSuccess && result.data) {
                            let options = result.data.map((tag) => {
                                return Utility.toReactSelectOption(tag, tag.name, tag.name);
                            });

                            const newTagMatch = _.find(options, (tag) => {
                                return tag.name.toLowerCase().trim() === newTag.label.toLowerCase().trim();
                            });

                            if (newTagMatch === undefined)
                                options = [newTag, ...options];

                            options = [...options];
                            resolve({ options });
                        }
                        else
                            console.error(result.errors.join("; "));
                    });
            }, 375);
        }).then((data) => {
            return data;
        });
    }

    private toggleChangeProfilePicture() {
        this.setState({ ...this.state, changeProfilePhoto: !this.state.changeProfilePhoto });
    }

    private toggleTypeChange(isAccepted: boolean) {
        this.setState({
            ...this.state,
            showTypeChangeWarning: !this.state.showTypeChangeWarning,
            typeChangeAccepted: isAccepted,
        });
    }

    private toggleTypeChangeWarning() {
        this.setState({
            ...this.state,
            showTypeChangeWarning: !this.state.showTypeChangeWarning,
        });
    }

    private validateAuthors() {
        const formModel = this.getFormModel() as Forms.Abstract.ComposeAuthorship;
        const model = formModel.data;
        const type = this.props.types.find((x: M.Abstract.AbstractType) => x.id === (model.typeId || 0));
        if (type) {
            // validate ASRS co-author if type requires it and current user is not a member
            if (type.requiresMemberAuthor && !this.props.submittingUser.isMember) {
                const hasMemberAuthor = _.filter(model.authors, (x) => !x.deleted && x.isMember).length > 0;
                if (!hasMemberAuthor) {
                    this.handleValidationErrors([{
                        inputName: "authors",
                        errors: ["You need to have at least one co-author who is a member of the ASRS"],
                    }], "authors", true);
                    return false;
                }
            }

            if (type.coAuthorsMembersOnly) {
                const errors: string[] = [];
                for (const a of model.authors) {
                    if (!a.isMember && !a.deleted)
                        errors.push(`${M.Generic.PersonHelper.getFullName(a)} is not a member of the ASRS`);
                }
                if (errors.length) {
                    this.handleValidationErrors([{
                        inputName: "authors",
                        errors,
                    }], "authors", true);
                    return false;
                }
            }
        }

        this.handleValidationErrors([], "authors", false);

        return true;
    }
}



// WEBPACK FOOTER //
// ./src/containers/abstracts/composer/components/AuthorshipForm.tsx